import { CanActivate, ExecutionContext, ForbiddenException, Injectable, UnauthorizedException } from '@nestjs/common';
import { Request } from 'express';
import * as jwt from 'jsonwebtoken';
import { PrismaService } from '../../prisma/prisma.service';

// Same fallback the cart API uses (api/src/app/auth/auth.service.ts) — the
// two services must agree on the secret for verification to work at all, so
// mirroring the API's dev default keeps an un-configured local boot working.
const DEV_JWT_SECRET = 'local-development-auth-secret';

// Merchant-side roles answer for their own store; platform_admin/operator
// answer across every store (operator per the Support Center spec: can pick
// chats and update status platform-wide, just not assign/transfer — that
// finer-grained distinction is enforced in admin's own permission system,
// not here). Everyone else (shoppers, accountants) is not an agent no matter
// what page they found the endpoint from. 'admin' is the legacy role the dev
// seed's store_demo owner carries.
const STORE_AGENT_ROLES = new Set(['merchant', 'store_manager', 'admin']);
const PLATFORM_AGENT_ROLES = new Set(['platform_admin', 'super_admin', 'operator']);

interface AgentTokenPayload {
  sub?: string;
  email?: string;
  name?: string;
  role?: string;
  storeId?: string;
}

export interface AgentIdentity {
  userId: string;
  email: string;
  name: string;
  role: string;
  // null = platform-wide agent (may act on every application); otherwise the
  // single application this agent is allowed to touch.
  applicationId: string | null;
}

export interface AgentScopedRequest extends Request {
  agent: AgentIdentity;
}

// The agent-side counterpart to ApiKeyGuard: instead of an application API
// key, the caller presents the cart platform's own login JWT. Verifying it
// here (shared HS256 secret) means chat-service needs no callback to the cart
// API and no agent credential of its own — the admin's existing session IS
// the credential, and their storeId claim routes them to the one application
// mapped to that store.
@Injectable()
export class AgentJwtGuard implements CanActivate {
  constructor(private readonly prisma: PrismaService) {}

  async canActivate(context: ExecutionContext): Promise<boolean> {
    const request = context.switchToHttp().getRequest<AgentScopedRequest>();
    const payload = this.verifyToken(this.extractToken(request));

    const role = payload.role ?? '';
    if (!payload.sub || (!STORE_AGENT_ROLES.has(role) && !PLATFORM_AGENT_ROLES.has(role))) {
      throw new ForbiddenException('Your role does not have support-agent access');
    }

    request.agent = {
      userId: payload.sub,
      email: payload.email ?? '',
      name: payload.name ?? payload.email ?? 'Support agent',
      role,
      applicationId: PLATFORM_AGENT_ROLES.has(role) ? null : await this.resolveApplicationId(payload),
    };

    return true;
  }

  private extractToken(request: Request): string {
    const [scheme, token] = request.headers.authorization?.split(' ') ?? [];

    if (scheme !== 'Bearer' || !token) {
      throw new UnauthorizedException('Authorization bearer token is required');
    }

    return token;
  }

  private verifyToken(token: string): AgentTokenPayload {
    try {
      return jwt.verify(token, process.env.AUTH_JWT_SECRET ?? DEV_JWT_SECRET) as AgentTokenPayload;
    } catch {
      // Expired and forged tokens get the same answer — no oracle.
      throw new UnauthorizedException('Invalid or expired token');
    }
  }

  private async resolveApplicationId(payload: AgentTokenPayload): Promise<string> {
    if (!payload.storeId) {
      // Old token minted before storeId was added to the payload — a fresh
      // login fixes it, so say so instead of a bare 403.
      throw new ForbiddenException('Your session has no store attached — please log in again');
    }

    const application = await this.prisma.application.findUnique({
      where: { externalStoreId: payload.storeId },
    });

    if (!application || !application.isActive || application.deletedAt || application.status !== 'active') {
      throw new ForbiddenException('No active chat application is mapped to your store');
    }

    return application.id;
  }
}
